﻿import { useCallback, useState } from 'react';

import { lerOrdemDasColunas, salvarOrdemDasColunas } from '@/lib/preferencias';

/**
 * Colunas da tabela de ações e a ordem escolhida pelo usuário.
 *
 * A ordem fica no localStorage do navegador: é preferência de tela, não dado de negócio.
 */

export const colunasPadrao = [
  'numero',
  'assunto',
  'situacao',
  'prazo',
  'areaResponsavel',
  'areaSesmt',
  'modulo',
  'prioridade',
  'clientes',
  'task',
  'atualizada',
] as const;

export type ColunaDaTabela = (typeof colunasPadrao)[number];

export const rotuloDaColuna: Record<ColunaDaTabela, string> = {
  numero: 'Número',
  assunto: 'Assunto',
  situacao: 'Situação',
  prazo: 'Prazo',
  areaResponsavel: 'Área responsável',
  areaSesmt: 'Área SESMT',
  modulo: 'Módulo',
  prioridade: 'Prioridade',
  clientes: 'Clientes',
  task: 'Task',
  atualizada: 'Atualizada',
};

/**
 * Lê a ordem salva e a reconcilia com as colunas atuais: ignora ids desconhecidos e
 * acrescenta no fim as colunas novas, para uma coluna criada depois não sumir.
 */
function carregar(): ColunaDaTabela[] {
  try {
    const bruto = lerOrdemDasColunas();
    const salvas: unknown = bruto === null ? [] : JSON.parse(bruto);
    const validas = Array.isArray(salvas)
      ? salvas.filter((c): c is ColunaDaTabela => (colunasPadrao as readonly unknown[]).includes(c))
      : [];
    const semRepetidas = [...new Set(validas)];

    return [...semRepetidas, ...colunasPadrao.filter((c) => !semRepetidas.includes(c))];
  } catch {
    return [...colunasPadrao];
  }
}

export function useOrdemDasColunas() {
  const [ordem, definirOrdem] = useState<ColunaDaTabela[]>(carregar);

  const gravar = (nova: ColunaDaTabela[]) => {
    definirOrdem(nova);
    salvarOrdemDasColunas(JSON.stringify(nova));
  };

  /** Move `origem` para a posição que `destino` ocupa hoje. */
  const mover = useCallback(
    (origem: ColunaDaTabela, destino: ColunaDaTabela) => {
      if (origem === destino) {
        return;
      }

      const nova = ordem.filter((c) => c !== origem);
      const posicaoDoDestino = ordem.indexOf(destino);
      const posicaoDaOrigem = ordem.indexOf(origem);
      // Arrastando para a direita, entra depois do destino; para a esquerda, antes.
      nova.splice(
        posicaoDaOrigem < posicaoDoDestino ? nova.indexOf(destino) + 1 : nova.indexOf(destino),
        0,
        origem,
      );
      gravar(nova);
    },
    [ordem],
  );

  const restaurar = () => {
    gravar([...colunasPadrao]);
  };

  return { ordem, mover, restaurar };
}
